import type { StaffImportReviewRowDetail } from "@lafayette/schemas";
import {
  fieldError,
  groupBox,
  groupLegend,
  hintText,
  listRow,
} from "../../../catalog/components/styles";
import { Button } from "../../../ui/button";
import { cn } from "../../../ui/cn";
import { CheckboxField, SelectField } from "../../../ui/fields";
import { Notice } from "../../../ui/notice";
import { RadioInput, RadioRow } from "../../../ui/radio";
import { rawText } from "../../components/styles";
import {
  type DecisionDraft,
  type DecisionState,
  emptyDraft,
  KEEP_AS_TEXT,
  type RowDrafts,
  updateTarget,
} from "../decisions";
import { describeAmounts, percent, targetFieldLabel } from "../format";
import { decisionKindLabels, matchBasisLabels } from "../labels";

type DecisionFormProps = Readonly<{
  state: DecisionState;
  detail: StaffImportReviewRowDetail;
  drafts: RowDrafts;
  /** The sentence to show when the staff member tried to go on without this decision. */
  error: string | undefined;
  disabled: boolean;
  onChange: (draft: DecisionDraft) => void;
}>;

export const decisionFieldId = (state: Pick<DecisionState, "required">): string =>
  `decision-${state.required.kind.toLowerCase()}-${state.required.fieldCode ?? "row"}`;

/** The sentence that says what is missing from a decision. A code, never text from the file. */
export function missingDecisionText(state: Pick<DecisionState, "required">): string {
  const { kind, fieldCode } = state.required;
  switch (kind) {
    case "CURRENCY":
      return "Choose the currency, or keep the price as text.";
    case "TIME_ZONE":
      return "Confirm the time zone.";
    case "DATE_ORDER":
      return "Choose the date order.";
    case "TAXONOMY":
      return `Choose a value for ${fieldCode === null ? "this field" : targetFieldLabel(fieldCode)}.`;
    case "DUPLICATE_ACTION":
      return "Choose what to do with the possible duplicate.";
    case "DESTRUCTIVE_UPDATE":
      return "Confirm that the import may replace these values.";
  }
}

function Savedline({ state }: Readonly<{ state: DecisionState }>) {
  if (state.saved === null) return null;
  return (
    <p className="m-0 text-sm leading-5 text-success">
      <span aria-hidden="true">✓ </span>
      Saved{state.saved.decidedByName === null ? "" : ` by ${state.saved.decidedByName}`}
      {state.saved.carriedFromDecisionId === null ? "" : " (carried over from an earlier try)"}.
    </p>
  );
}

/** One decision the row owes, as a labelled control. The choice is kept on the page until it is saved. */
export function DecisionForm({
  state,
  detail,
  drafts,
  error,
  disabled,
  onChange,
}: DecisionFormProps) {
  const { required, shown } = state;
  const id = decisionFieldId(state);
  const title =
    required.kind === "TAXONOMY" && required.fieldCode !== null
      ? `${decisionKindLabels.TAXONOMY}: ${targetFieldLabel(required.fieldCode)}`
      : decisionKindLabels[required.kind];

  return (
    <div className={cn(listRow, "grid gap-2 p-4")} data-decision={required.kind}>
      <h4 className="m-0 text-base font-semibold">{title}</h4>
      {renderControl()}
      <Savedline state={state} />
    </div>
  );

  function renderControl() {
    switch (required.kind) {
      case "CURRENCY": {
        const options = required.options.map((option) => ({
          value: option.code,
          label: option.label,
        }));
        if (!options.some((option) => option.value === KEEP_AS_TEXT)) {
          options.push({ value: KEEP_AS_TEXT, label: "Keep as text (the price becomes unknown)" });
        }
        const price = detail.proposal?.price ?? null;
        return (
          <SelectField
            id={id}
            label="Currency"
            hint={
              price === null
                ? "The currency is not clear from the file."
                : `The file gives the amounts (${describeAmounts(price)}) but not the currency. Choose it. A $ sign does not say which dollar.`
            }
            value={shown.chosenCode ?? ""}
            options={options}
            placeholder="Choose a currency"
            error={error}
            required
            disabled={disabled}
            onChange={(value) =>
              onChange(value === "" ? emptyDraft : { ...emptyDraft, chosenCode: value })
            }
          />
        );
      }
      case "TIME_ZONE": {
        const suggested = required.suggestedCode;
        return (
          <CheckboxField
            id={id}
            label={
              suggested === null ? "Confirm the time zone" : `Confirm the time zone ${suggested}`
            }
            hint={
              <>
                The file has no time zone. It was guessed from the city. You can only confirm this
                zone. To use another zone, skip the row and fix the file.
                {error === undefined ? null : (
                  <span id={`${id}-error`} className={fieldError}>
                    {" "}
                    <span aria-hidden="true">⚠ </span>
                    {error}
                  </span>
                )}
              </>
            }
            checked={shown.chosenCode !== null && shown.chosenCode === suggested}
            disabled={disabled || suggested === null}
            onChange={(checked) =>
              onChange(
                checked && suggested !== null
                  ? { ...emptyDraft, chosenCode: suggested }
                  : emptyDraft,
              )
            }
          />
        );
      }
      case "DATE_ORDER":
        return (
          <SelectField
            id={id}
            label="Date order"
            value={shown.chosenCode ?? ""}
            options={[
              { value: "DMY", label: "Day first (31/12/2027)" },
              { value: "MDY", label: "Month first (12/31/2027)" },
            ]}
            placeholder="Choose the date order"
            error={error}
            required
            disabled={disabled}
            onChange={(value) =>
              onChange(value === "" ? emptyDraft : { ...emptyDraft, chosenCode: value })
            }
          />
        );
      case "TAXONOMY": {
        const options = required.options.map((option) => ({
          value: option.code,
          label: option.label,
        }));
        const labelOf = (code: string | null) =>
          required.options.find((option) => option.code === code)?.label ?? code ?? "";
        const assist = detail.assists.find(
          (entry) =>
            entry.fieldCode === required.fieldCode && entry.choiceCode !== null && !entry.accepted,
        );
        return (
          <>
            {assist === undefined ? null : (
              <fieldset className="m-0 grid min-w-0 justify-items-start gap-1 rounded-sm border border-dashed border-primary bg-accent p-4">
                <legend className="sr-only">Model suggestion</legend>
                <p className="m-0">
                  <strong>A model suggests: {labelOf(assist.choiceCode)}</strong>
                </p>
                <p className={hintText} id={`${id}-assist`}>
                  Model {assist.modelId}, version {assist.modelVersion}, prompt{" "}
                  {assist.promptVersion}. Confidence {percent(assist.confidenceBp)}. It is only a
                  suggestion. It is used only if you accept it.
                </p>
                {shown.assistId === assist.id ? (
                  <p role="status" className="m-0 text-success">
                    <span aria-hidden="true">✓ </span>Suggestion accepted. Save your decisions to
                    keep it.
                  </p>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    aria-describedby={`${id}-assist`}
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        ...emptyDraft,
                        chosenCode: assist.choiceCode,
                        assistId: assist.id,
                      })
                    }
                  >
                    Accept suggestion
                  </Button>
                )}
              </fieldset>
            )}
            <SelectField
              id={id}
              label={targetFieldLabel(required.fieldCode ?? "category")}
              hint="Choose from the values the catalog knows."
              value={shown.chosenCode ?? ""}
              options={options}
              placeholder="Choose a value"
              error={error}
              required
              disabled={disabled}
              onChange={(value) =>
                onChange(value === "" ? emptyDraft : { ...emptyDraft, chosenCode: value })
              }
            />
          </>
        );
      }
      case "DUPLICATE_ACTION":
        return (
          <fieldset
            id={id}
            tabIndex={-1}
            className={cn(groupBox, "gap-1")}
            aria-describedby={error === undefined ? undefined : `${id}-error`}
          >
            <legend className={groupLegend}>What should the import do with this row?</legend>
            {detail.candidates.map((candidate) => {
              const checked =
                shown.chosenCode === "LINK_AS_UPDATE" &&
                shown.chosenCatalogItemId === candidate.catalogItemId;
              return (
                <RadioRow key={candidate.catalogItemId} className="items-start py-2">
                  <RadioInput
                    name={id}
                    id={`${id}-link-${candidate.catalogItemId}`}
                    checked={checked}
                    disabled={disabled}
                    aria-invalid={error === undefined ? undefined : true}
                    onChange={() =>
                      onChange({
                        ...emptyDraft,
                        chosenCode: "LINK_AS_UPDATE",
                        chosenCatalogItemId: candidate.catalogItemId,
                      })
                    }
                  />
                  <span>
                    Update the existing item <strong>{candidate.name}</strong> (
                    {matchBasisLabels[candidate.matchBasis].toLowerCase()})
                  </span>
                </RadioRow>
              );
            })}
            <RadioRow className="items-start py-2">
              <RadioInput
                name={id}
                checked={shown.chosenCode === "CREATE_NEW"}
                disabled={disabled}
                aria-invalid={error === undefined ? undefined : true}
                onChange={() => onChange({ ...emptyDraft, chosenCode: "CREATE_NEW" })}
              />
              <span>Create a new item. The existing items stay as they are.</span>
            </RadioRow>
            <RadioRow className="items-start py-2">
              <RadioInput
                name={id}
                checked={shown.chosenCode === "SKIP"}
                disabled={disabled}
                aria-invalid={error === undefined ? undefined : true}
                onChange={() => onChange({ ...emptyDraft, chosenCode: "SKIP" })}
              />
              <span>Skip this row. Nothing is imported from it.</span>
            </RadioRow>
            {error === undefined ? null : (
              <p id={`${id}-error`} className={fieldError}>
                <span aria-hidden="true">⚠ </span>
                {error}
              </p>
            )}
          </fieldset>
        );
      case "DESTRUCTIVE_UPDATE": {
        const target = updateTarget(detail, drafts);
        const replaced = target?.changes.filter((change) => change.destructive) ?? [];
        return (
          <>
            {target === null ? (
              <Notice tone="warning">Choose the existing item to update first.</Notice>
            ) : (
              <>
                <p>
                  This update replaces values that <strong>{target.name}</strong> already has:
                </p>
                <ul className="m-0 grid gap-1 pl-5">
                  {replaced.map((change) => (
                    <li key={change.fieldCode}>
                      <strong>{targetFieldLabel(change.fieldCode)}</strong>: now{" "}
                      <span className={rawText}>{change.current ?? "(empty)"}</span>, the file says{" "}
                      <span className={rawText}>{change.proposed ?? "(empty)"}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <CheckboxField
              id={id}
              label="I confirm that the import may replace these values"
              hint={
                error === undefined ? undefined : (
                  <span id={`${id}-error`} className={fieldError}>
                    <span aria-hidden="true">⚠ </span>
                    {error}
                  </span>
                )
              }
              checked={
                target !== null &&
                shown.chosenCatalogItemId === target.catalogItemId &&
                shown.confirmedChangeSha256 !== null &&
                shown.confirmedChangeSha256 === target.changeSha256
              }
              disabled={disabled || target === null || target.changeSha256 === null}
              onChange={(checked) =>
                onChange(
                  checked && target !== null
                    ? {
                        ...emptyDraft,
                        chosenCatalogItemId: target.catalogItemId,
                        confirmedChangeSha256: target.changeSha256,
                      }
                    : emptyDraft,
                )
              }
            />
          </>
        );
      }
    }
  }
}
